import ErrorCatch from './ErrorCatch';
import Task from './Task';
import WindowOpen from './WindowOpen';
import MeaningfulPaint from './MeaningfulPaint';
import MobxAction from './MobxAction';
import ReactRender from './ReactRender';
import { getHookRegistryFromEcode } from './EcodeRegHooks';
import history from '../history-plugin/history';
import { isEqual } from '../dateFormat/common';

import { getUuid, createCustomEvent } from './utils';
import { TaskType, TaskItemData, AnyObj } from './types';
import { checkWeId } from '../reactUtils';
import { componentCollections, getCollectedComponent, updateCollectEnable } from './ComponentCollection';

const initMonitorSwitchStatus: AnyObj = {
  master: true, // 总开关
  traceId: localStorage.getItem('weappMonitorTraceIdSwitchStatus') === 'true' ? true : false, // traceid开关
  route: false, // 路由 RMP 开关
  render: false, // 渲染开关
  mobxAction: false, // mobxAction开关
}

const getInitPageMonitorSwitchStatus = () => {
  const sessionStatusJson = sessionStorage.getItem('weappMonitorPageSwitchStatus');
  let initPageMonitorSwitchStatus: AnyObj = {
    master: false, // 总开关
    traceId: true, // traceid开关
    route: false, // 路由 RMP 开关
    render: false, // 渲染开关
    mobxAction: false, // mobxAction开关
  }
  if (sessionStatusJson) {
    try {
      const status = JSON.parse(sessionStatusJson);
      if (typeof status === 'object') {
        initPageMonitorSwitchStatus = { ...initPageMonitorSwitchStatus, ...status };
      }
    } catch (error) {
    }
  }
  return initPageMonitorSwitchStatus;
}

// 单页调试开关: sessionStorage中取初始化的开关状态
const initPageMonitorSwitchStatus = getInitPageMonitorSwitchStatus();

class Monitor {
  private reportDelayTime: number = 3000; // 延迟上报时间
  private timeInterval: number = 300000; // 5min 上报一次
  private timer: any = null;
  private taskTimer: any = null;
  private reportId: string = getUuid();
  private taskInstance: Task | any = null; // 任务实例: 主要包含添加任务,上报任务的方法。所有需要上报的任务数据都存储在实例中
  private windowOpenInstance: any = null; // window open实例
  private fmpInstance: MeaningfulPaint | null = null; // FMP监控实例
  private rmpInstance: MeaningfulPaint | null = null; // RMP监控实例
  private renderInstance: ReactRender | null = null; // react 节点渲染耗时分析
  private errorCatchInstance: any = null; // 错误捕获实例
  private actionInstance: MobxAction | null = null;
  private domNodeInsertedListener: any = null; // DOMNodeInserted 事件监听
  private domNodeListening: boolean = false; // 是否启用DOMNodeInserted的数据上报
  private routeUnlistener: (() => void) | null = null;

  // 租户维度 监控的开关状态
  public status: AnyObj = initMonitorSwitchStatus;
  // 个人维度 监控的开关状态 （供调试小工具使用）
  public pageStatus: AnyObj = initPageMonitorSwitchStatus;

  public monitorPerfData: any = [];

  /**
   * 初始化Monitor
  */
  public initMonitor = (reportDelayTime?: number) => {
    // 支持初始化传入延迟时间
    this.reportDelayTime = reportDelayTime ? reportDelayTime : reportDelayTime === 0 ? 0 : 3000;
    
    // localstore开关: 可强制终止上报
    if (localStorage.getItem('stopWeappMonitor')) return;

    // 数据收集上报机制
    this.updateMonitor(true);
  }

  /**
   * 销毁Monitor: 销毁所有事件监听 + 重置所有实例
   */
  public destory = () => {
    this.taskTimer && clearTimeout(this.taskTimer);
    this.taskTimer = null;
    // 5分钟上报一次事件监听
    this.timer && clearInterval(this.timer);
    this.timer = null;
    // task
    this.taskInstance = null;
    // error
    this.errorCatchInstance?.destroy?.();
    this.errorCatchInstance = null;
    // window open
    this.windowOpenInstance = null;
    // fmp
    this.fmpInstance?.stopTrace(true);
    this.fmpInstance = null;
    // rmp
    this.rmpInstance?.stopTrace(true);
    this.rmpInstance = null;
    this.routeUnlistener?.()
    // render
    this.renderInstance?.stopProfiling();
    this.renderInstance = null;
    // mobxAction
    this.actionInstance?.unlisten?.();
    this.routeUnlistener = null;
    // DOMNodeInserted 事件监听 
    // if (this.domNodeInsertedListener) {
    //   const root = window.document.getElementById('root');
    //   root?.removeEventListener('DOMNodeInserted', this.domNodeInsertedListener);
    //   this.domNodeInsertedListener = null;
    // }
  }

  // 更新监控
  updateMonitor = (isInitUpdate?: boolean) => {
    const catchMain = this.status.master || this.pageStatus.master;

    if (catchMain) {
      // 1-实例化Task（跟随总开关）
      if (!this.taskInstance) {
        this.taskInstance = new Task({
          reportId: this.reportId,
          updatePerfData: this.updatePerfData
        });
      }

      // 2-错误捕获（跟随总开关）
      if (!this.errorCatchInstance) {
        this.errorCatchInstance = new ErrorCatch({ addTask: this.addTask });
        this.errorCatchInstance.init();
      }

      // 3-window.open监听（跟随总开关）
      if (!this.windowOpenInstance) {
        this.windowOpenInstance = new WindowOpen({ addTask: this.addTask });
        this.windowOpenInstance.init(isInitUpdate);
      }

      // 4-FMP（跟随总开关）
      if (!this.fmpInstance) {
        // 实例化主内容渲染监控
        this.fmpInstance = new MeaningfulPaint({
          type: 'first', callback: () => {
            // 标记 FMP 时间
            this.addTask('markPoint', {}, 'fmp');
            // FMP拿到后上报一次。防止丢失
            this.submitTask(isInitUpdate, false, 'fmp');
            // 清除 mp 监控
            this.fmpInstance = null;
            // 在 fmp 之后去获取 ecode 组件复写
            // getHookRegistryFromEcode().then(overwrites => {
            //   this.addTask('markPoint', (overwrites || {}) as AnyObj, 'ecode');
            // });
          }
        });
      }

      // 5-路由RMP（单独开关控制）
      this.updateRouteMonitor();

      // 6-MobxAction（单独开关控制）
      this.updateMobxActionMonitor();

      // 7-render（单独开关控制）
      this.updateRenderMonitor();

      // 8-DOMNodeInserted事件监听（跟随总开关） 
      // 初始化的时候监听一次，然后销毁，后续不进行监听
      // 调整：暂不进行 DOMNodeInserted的事件监听 
      if (!this.domNodeInsertedListener && isInitUpdate) {
        const root = window.document.getElementById('root');
        if (root) {
          // this.domNodeListening = true;
          this.fmpInstance?.startTrack?.();
          this.rmpInstance?.startTrack?.();
          // this.domNodeInsertedListener = this.onDOMNodeInserted;
          // root?.addEventListener('DOMNodeInserted', this.onDOMNodeInserted);
        }
      }

      // 9-租户总开关开启: 5分钟上报一次
      if (this.status.master) {
        if (!this.timer) {
          this.timer = setInterval(() => {
            if (this.timer) {
              clearInterval(this.timer)
            }
            this.submitTask(isInitUpdate);
          }, this.timeInterval);
        } 
      } else {
        this.timer && clearInterval(this.timer);
        this.timer = null;
      }
    } else { // 销毁所有事件监听和实例
      this.destory();
    }
  }

  // 更新MobxAction监听
  updateMobxActionMonitor = () => {
    const catchMobxAction = this.status.mobxAction || this.pageStatus.mobxAction;

    if (catchMobxAction) {
      if (!this.actionInstance) {
        this.actionInstance = new MobxAction();
      }
      // 开启mobx action监控
      this.actionInstance?.listen((actionInfo) => {
        // 标记 action 执行时间
        this.addTask('markPoint', actionInfo, 'action');
      })
    } else {
      this.actionInstance?.unlisten?.();
      this.actionInstance = null;
    }
  }

  // 更新render监听
  updateRenderMonitor = () => {
    const catchRender = this.status.render || this.pageStatus.render;
    if (catchRender) {
      if (!this.renderInstance) {
        this.renderInstance = new ReactRender({
          callback: (renderInfo) => {
            // 上报 render 耗时
            this.addTask('markPoint', renderInfo, 'render');
          }
        });
      }
      this.renderInstance.startProfiling();
    } else {
      this.renderInstance?.stopProfiling();
    }
  }

  // 更新路由监听
  updateRouteMonitor = () => {
    const catchRender = this.status.route || this.pageStatus.route;

    if (catchRender) {
      this.routeUnlistener = history.listen(() => {
        // 1 - 路由页面渲染监控
        if (!this.rmpInstance) {
          // 开启 RMP 渲染监控
          this.rmpInstance = new MeaningfulPaint({
            type: 'route', callback: (mpInfo) => {
              // 上报本次路由的 RMP 时间点
              this.addTask('markPoint', mpInfo, 'rmp');
            }
          });
        }
        // 停止计算上次 MP 计算
        this.rmpInstance?.stopTrace(true);
        // 开启本次路由切换 MP 计算
        this.rmpInstance?.startTrack();
      })
    } else {
      // 关闭 RMP 渲染监控
      this.rmpInstance?.stopTrace(true);
      this.rmpInstance = null;
    }

    // 2 - 切路由上报
    this.addTask('markPoint', {}, 'route');

    // this.routeUnlistener = history.listen(() => {
    //   // 1 - 路由页面渲染监控
    //   if (catchRender) {
    //     if (!this.rmpInstance) {
    //       // 开启 RMP 渲染监控
    //       this.rmpInstance = new MeaningfulPaint({
    //         type: 'route', callback: (mpInfo) => {
    //           // 上报本次路由的 RMP 时间点
    //           this.addTask('markPoint', mpInfo, 'rmp');
    //         }
    //       });
    //     }
    //     // 停止计算上次 MP 计算
    //     this.rmpInstance?.stopTrace(true);
    //     // 开启本次路由切换 MP 计算
    //     this.rmpInstance?.startTrack();
    //   } else {
    //     // 关闭 RMP 渲染监控
    //     this.rmpInstance?.stopTrace(true);
    //     this.rmpInstance = null;
    //   }

    //   // 2 - 切路由上报
    //   this.addTask('markPoint', {}, 'route');
    // })
  }

  // 捕获页面中重复的weId
  checkDuplicatedWeId = (callback?: (reportId: string) => void) => {
    const root = document.getElementById('root');
    if (root) {
      const duplicatedWeids = checkWeId(root) || {};
      this.addTask('markPoint', { duplicatedWeids: JSON.stringify(duplicatedWeids) }, 'weId');
      this.submitTask(false, undefined, undefined, callback);
    }
  }

  // DOMNodeInserted 事件监听
  onDOMNodeInserted = (e: Event) => {
    if (this.domNodeListening) {
      this.addTask('markPoint', {}, 'fpt'); // 标记白屏耗时
      this.submitTask(true, true, 'fpt');

      // 更新Task参数: DOMNodeInserted事件后,错误数据可按照正常的5分钟上报一次进行上报
      this.taskInstance?.updateSubmitErrorTaskNow(false);

      this.domNodeListening = false;
    }

    this.fmpInstance?.onDOMNodeInserted(e);
    this.rmpInstance?.onDOMNodeInserted(e);

    // 立即销毁
    if (this.domNodeInsertedListener) {
      const root = window.document.getElementById('root');
      root?.removeEventListener('DOMNodeInserted', this.domNodeInsertedListener);
      this.domNodeInsertedListener = null;
    }
  }

  // 添加待上报任务
  addTask = (type: TaskType, data: TaskItemData, markDes?: string) => {
    this.taskInstance?.addTask(type, data, markDes, (markData: AnyObj) => {
      // fmp|ecode|表单审批最后一次上报,埋点上报的同时,通知监控工具
      const needNotify =
        (type === 'markPoint' && (markDes === 'ecode' || markDes === 'fmp' || markDes === 'fpt')) ||
        (type === 'custom' && data?.type.indexOf('workflow_flowpage') > -1)
      if (needNotify) {
        this.notifyMonitoringTool(data, markData, markDes || data?.type);
      }
    });
  }

  // 上报所有任务
  submitTask = (isInitUpdate?: boolean, isInit?: boolean, submitType?: string, callback?: (reportId: string) => void) => {
    const params = [
      isInit,
      submitType,
      () => {
        callback?.(this.reportId);
      },
      ((status: AnyObj) => {
        // 更新开关状态
        this.updateMonitorSwitchStatus(status);
      }),
      (entries: any[]) => {
        this.updatePerfData(entries)
      }
    ]
    if (isInitUpdate) {
      if (this.taskTimer) {
        clearTimeout(this.taskTimer);
        this.taskTimer = null;
      }
      this.taskTimer = setTimeout(() => {
        this.taskInstance?.submitTask(
          ...params
        );
        clearTimeout(this.taskTimer);
      }, this.reportDelayTime)
    } else {
      this.taskInstance?.submitTask(
        ...params
      );
    }
  }

  /**
   * 通知监控工具 （fpt fmp ecode日志 业务埋点）
   * @param data 传入addTask方法的数据
   * @param markData addTask生成的数据
   * @param type 当前上报的特殊标记 ('fmp' | 'ecode' 等)
   */
  notifyMonitoringTool = (data: any, markData: AnyObj, type: string) => {
    let notifyEvent: any = createCustomEvent('weappMonitorNotify', { data, markData, type });
    window.dispatchEvent(notifyEvent);
    notifyEvent = null;
  }

  getReportId = () => this.reportId;

  // 获取第一次上报的主任务数据
  getTaskData = () => this.taskInstance?.getTaskData?.()

  // 获取监控开关状态
  getMonitorSwitchStatus = (name?: string) => {
    if (name) return this.status[name];
    return this.status;
  }

  componentCollections = componentCollections;

  getComponentCollection = getCollectedComponent;

  updateComponentCollectionSwitch = updateCollectEnable;

  // 更新监控开关状态
  updateMonitorSwitchStatus = (newStatus: AnyObj) => {
    const oldStatus = this.status;
    const status = { ...oldStatus, ...newStatus }
    this.status = status;

    // 更新traceId开关状态
    if (this.status.traceId !== oldStatus.traceId) {
      localStorage.setItem('weappMonitorTraceIdSwitchStatus', JSON.stringify(this.status.traceId));
    }
    // 开关状态变更,更新实例或事件监听
    if (!isEqual(oldStatus, this.status)) {
      this.updateMonitor(false);
    }
    return this.status;
  }

  // 获取个人维度监控开关状态
  getPageMonitorSwitchStatus = (name?: string) => {
    if (name) return this.pageStatus[name];
    return this.pageStatus;
  }

  // 更新个人维度监控开关状态
  updatePageMonitorSwitchStatus = (newStatus: AnyObj) => {
    const oldStatus = this.pageStatus;
    this.pageStatus = { ...this.pageStatus, ...newStatus };
    // 开关状态变更,更新实例或事件监听
    if (!isEqual(oldStatus, this.pageStatus)) {
      this.updateMonitor(false);
    }
    return this.pageStatus;
  }

  // 更新任意数据
  updateParams = (params: AnyObj) => {
    Object.keys(params).forEach(key => {
      if (key in this) {
        // @ts-ignore
        this[key] = params[key];
      }
    })
  }

  // 存储FMP之前的perforamnce resource数据
  updatePerfData = (entries: any[]) => {
    this.monitorPerfData = [
      ...this.monitorPerfData,
      ...entries
    ];
  }

  // 获取存储的performance resource数据
  getPerfData = () => this.monitorPerfData

  // 获取注册的ecode工具
  getRegistryEcode = () => {
    getHookRegistryFromEcode().then(overwrites => {
      this.addTask('markPoint', (overwrites || {}) as AnyObj, 'ecode');
    });
  }
}

const weappMonitorInstance = new Monitor();

// 增加自定义的监听事件,以便通知门户等在utils前加载的情况,监控实例已经初始化成功
let customEvent: any = createCustomEvent('weappMonitorInstanceInited', { weappMonitorInstance });
window.dispatchEvent(customEvent);
customEvent = null;

export default weappMonitorInstance;